import React, { createContext, useState } from 'react';

import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { usePagination } from '@shared/hooks/usePagination';
import { AddPaymentModal } from '@widgets/AddPayments/ui/AddPaymentModal/AddPaymentModal';
import { fetchRemovePayment } from '@widgets/PaymentsList/lib/model/PaymentListApi';
import { EditPaymentModal } from '@widgets/PaymentsList/ui/EditPaymentModal/EditPaymentModal';
import { PaymentItem } from '@widgets/PaymentsList/ui/PaymentItem/PaymentItem';
import { PaymentsListSkeleton } from '@widgets/PaymentsList/ui/PaymentsListSkeleton/PaymentsListSkeleton';
import { TableHead } from '@widgets/PaymentsList/ui/TableHead/TableHead';

import cls from './PaymentsList.module.css';
import { icons } from '../../../components/icons/Icons';
import { Loader } from '../../../components/UI/NewLoader/Loader/Loader';
import { fetchPaymentsFilters } from '../../../store/slices/paymentSlice/action';
import { paymentActions, paymentSelector } from '../../../store/slices/paymentSlice/paymentSlice';

export const updateFilter = (dispatch, paymentActions, fetchPaymentsFilters, filter, page) => {
    const newFilter = { ...filter, page };

    const getQuery = Object.entries(newFilter)
        .filter(([key, value]) => value !== null)
        .map(([key, value]) => (key === 'page' ? `page=${value}` : value))
        .join('');

    dispatch(paymentActions.changeFilters(newFilter));
    dispatch(paymentActions.changeIsActiveFilters(true));
    dispatch(
        fetchPaymentsFilters(
            `${routable.getRoute('searchCompanyPaymentOperationsReact')}?${getQuery}`
        )
    );
};
export const Context = createContext({});
export const PaymentsList = () => {
    const { pagination, filter, isLoadingPayments, isLoadingFilters, isLoadingDataForEdit } =
        useSelector((state) => state.paymentReducer);

    const isActiveFilter = Object.entries(filter).some(([key, value]) => !!value && key !== 'page');

    const [isOpen, setIsOpen] = useState(false);
    const [currentPayment, setCurrentPayment] = useState(null);
    const dispatch = useDispatch();
    const { paginationArray } = usePagination();

    const allPayments = useSelector(paymentSelector.selectAll);

    const [addModalState, setAddModalState] = useState({
        isOpen: false,
        mode: 'debit',
        step: 1,
        isBetweenAccount: false,
        isOpenPartners: false,
        addPaymentMode: 'repeat',
    });

    const onSwitcherChange = () => {
        setAddModalState((prevState) => ({
            ...prevState,
            isBetweenAccount: !prevState.isBetweenAccount,
        }));
    };

    const onHideModal = () => {
        setIsOpen(false);
        setCurrentPayment(null);
    };

    const onHideAddModal = () => {
        const newState = { ...addModalState, isOpen: false };
        setAddModalState(newState);
        setCurrentPayment(null);
    };

    const onHideModalPartner = () => {
        const newState = { ...addModalState, isOpenPartners: false };
        setAddModalState(newState);
    };

    const contextValue = {
        setCurrentPayment,
        setIsOpen,
        currentPayment,
    };

    const isLoading = isLoadingFilters || isLoadingDataForEdit || isLoadingPayments;

    if (isLoading) {
        return <PaymentsListSkeleton />;
    }

    const deleteUndistributedPayment = async () => {
        loader.show();
        const callback = async (data) => {
            await updateFilter(
                dispatch,
                paymentActions,
                fetchPaymentsFilters,
                filter,
                pagination.current
            );
            await onHideAddModal();
            await loader.hide();
        };
        fetchRemovePayment(currentPayment.guid, callback);
    };

    return (
        <Context.Provider value={contextValue}>
            <>
                <div className={classNames(cls.tableWrapper)}>
                    {isOpen && (
                        <EditPaymentModal
                            isOpen={isOpen}
                            onShowModal={onHideModal}
                            payment={currentPayment}
                        />
                    )}
                    {addModalState.isOpen && (
                        <AddPaymentModal
                            isOpen={addModalState.isOpen}
                            onShowModal={onHideAddModal}
                            step={addModalState.step}
                            modalState={addModalState}
                            onHideModalPartner={onHideModalPartner}
                            onHideModal={onHideAddModal}
                            setModalState={setAddModalState}
                            onSwitcherChange={onSwitcherChange}
                            mode={addModalState.addPaymentMode}
                            payment={currentPayment}
                            deleteUndistributedPayment={deleteUndistributedPayment}
                        />
                    )}
                    {!!allPayments.length && (
                        <>
                            <TableHead />
                            {allPayments.map((payment) => (
                                <PaymentItem
                                    payment={payment}
                                    key={payment.guid}
                                    setAddModalState={setAddModalState}
                                />
                            ))}
                        </>
                    )}

                    {isLoadingPayments && <Loader />}
                    {isActiveFilter && !allPayments.length && (
                        <div className={classNames(cls.empty)}>
                            {icons.empty}
                            {i18next.t('PaymentsList.return.empty')}
                        </div>
                    )}
                </div>
                {!!allPayments.length && (
                    <div className={classNames(cls.paginationWrapper)}>
                        {pagination.current !== 1 && (
                            <span
                                onClick={async () => {
                                    await updateFilter(
                                        dispatch,
                                        paymentActions,
                                        fetchPaymentsFilters,
                                        filter,
                                        pagination.current - 1
                                    );
                                }}
                                className={classNames(cls.paginationItem)}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_5671_2459)">
                                        <g mask="url(#mask0_5671_2459)">
                                            <path
                                                d="M9.33301 12L5.33301 8L9.33301 4L10.2663 4.93333L7.19967 8L10.2663 11.0667L9.33301 12Z"
                                                fill="#1C1B1F"
                                            />
                                        </g>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5671_2459">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                        )}
                        {paginationArray.map((page, index) => (
                            <span
                                key={Date.now() + index}
                                onClick={async () => {
                                    if (page != pagination.current && page !== '...') {
                                        await updateFilter(
                                            dispatch,
                                            paymentActions,
                                            fetchPaymentsFilters,
                                            filter,
                                            page
                                        );
                                    }
                                }}
                                className={classNames(cls.paginationItem, {
                                    [cls.active]: page == pagination.current,
                                    [cls.disabled]: page === '...',
                                })}
                            >
                                {page}
                            </span>
                        ))}
                        {pagination.current !== pagination.count && !!pagination.count && (
                            <span
                                onClick={async () => {
                                    await updateFilter(
                                        dispatch,
                                        paymentActions,
                                        fetchPaymentsFilters,
                                        filter,
                                        pagination.current + 1
                                    );
                                }}
                                className={classNames(cls.paginationItem)}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_5671_2462)">
                                        <g mask="url(#mask0_5671_2462)">
                                            <path
                                                d="M8.39967 8L5.33301 4.93333L6.26634 4L10.2663 8L6.26634 12L5.33301 11.0667L8.39967 8Z"
                                                fill="#1C1B1F"
                                            />
                                        </g>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5671_2462">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                        )}
                    </div>
                )}
            </>
        </Context.Provider>
    );
};
